"use client";

import { BrandHeaderLogo } from "@/components/BrandHeaderLogo";
import { ChevronDown } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { MouseEvent } from "react";

import {
  type MarketingNavLink,
  parseMarketingNavHref,
  scrollToMarketingSection,
} from "./marketingNav";

type HeroV2NavbarProps = {
  links: readonly MarketingNavLink[];
  signInLabel: string;
  primaryCtaLabel: string;
};

function MarketingNavbarLink({ link }: { link: MarketingNavLink }) {
  const pathname = usePathname();
  const { isSectionLink, sectionId } = parseMarketingNavHref(link.href);

  const handleSectionClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isSectionLink || !sectionId || pathname !== "/") {
      return;
    }

    event.preventDefault();
    scrollToMarketingSection(sectionId);
  };

  return (
    <Link
      href={link.href}
      onClick={handleSectionClick}
      className="inline-flex items-center gap-2 text-[15px] font-semibold text-slate-700 transition-colors duration-150 hover:text-slate-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-600"
    >
      {link.label}
      {link.hasChevron ? <ChevronDown className="h-3.5 w-3.5" strokeWidth={2.2} aria-hidden /> : null}
    </Link>
  );
}

export function HeroV2Navbar({
  links,
  signInLabel,
  primaryCtaLabel,
}: HeroV2NavbarProps) {
  return (
    <header className="sticky top-0 z-50 w-full border-b border-slate-200/70 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90">
      <div className="mx-auto flex h-[76px] w-full max-w-[1440px] items-center justify-between px-5 sm:px-8 xl:px-10">
        <Link href="/" className="flex shrink-0 items-center" aria-label="darsi.ma home">
          <BrandHeaderLogo />
        </Link>

        <nav className="hidden items-center gap-10 lg:flex" aria-label="Main navigation">
          {links.map((link) => (
            <MarketingNavbarLink key={link.label} link={link} />
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            href="/sign-in"
            className="hidden h-11 items-center rounded-xl border border-slate-200 bg-white px-5 text-[15px] font-semibold text-slate-950 shadow-sm transition-colors duration-150 hover:border-slate-300 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-600 md:inline-flex"
          >
            {signInLabel}
          </Link>

          <Link
            href="/sign-up"
            className="inline-flex h-11 items-center rounded-xl bg-[#08A62F] px-4 text-sm font-bold text-white shadow-[0_12px_24px_-16px_rgba(8,166,47,0.8)] transition-colors duration-150 hover:bg-[#078F2B] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-green-700 sm:px-5 sm:text-[15px]"
          >
            {primaryCtaLabel}
          </Link>
        </div>
      </div>
    </header>
  );
}
